import { FC, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import dayjs from 'dayjs';

import './OutlookCallback.scss';
import {
  calendarApi,
  defaultOutlookCalScopes,
  generalSnackBarAtom,
  getWeekEdgesDateOf,
  useAuthContext,
  useSyncOutlookCalendarEvents,
  useSyncOutlookCalendarsList,
  useSubscribeOutlookCalendarWatch,
} from '../../../data-access';
import { Path } from '../../../routing';
import { useRecoilState } from 'recoil';

const OutlookCallback: FC = () => {
  const location = useLocation();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { syncOutlookCalendarsList } = useSyncOutlookCalendarsList();
  const { syncOutlookCalEvents } = useSyncOutlookCalendarEvents();
  const [_, setGeneralSnackbar] = useRecoilState(generalSnackBarAtom);
  const { subscribeOutlookCalendarWatch } = useSubscribeOutlookCalendarWatch();

  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get('code') ?? '';
  const url_state = searchParams.get('state');

  useEffect(() => {
    if (!user.userId || !code) {
      return;
    }

    const update = async () => {
      try {
        const tokens = await calendarApi.exchangeOutlookToken(
          user.userId,
          code,
          `${window.location.origin}/callback/outlook`,
          'v2.0',
          defaultOutlookCalScopes
        );

        if (tokens) {
          const newCalendarIds = await syncOutlookCalendarsList({
            userId: user.userId,
            scopes: defaultOutlookCalScopes,
            accessToken: tokens.data.accessToken,
            refreshToken: tokens.data.refreshToken,
            expiryDate: tokens.data.expiryDate,
            rootEmail: tokens.data.rootEmail,
          });

          const { startDate, endDate } = getWeekEdgesDateOf(dayjs());

          syncOutlookCalEvents({
            userId: user.userId,
            calendarIds: newCalendarIds,
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString(),
          });
          subscribeOutlookCalendarWatch({
            calendarIds: newCalendarIds,
            userId: user.userId,
          });
        }
      } catch (error) {
        setGeneralSnackbar('Something went wrong — try again?');
      } finally {
        // Use the state parameter as redirect URL if available, otherwise default to DASHBOARD
        const redirectUrl = url_state || Path.DASHBOARD;
        navigate(redirectUrl, { replace: true });
      }
    };

    update();
  }, [user?.userId, code, url_state]);

  return (
    <div className="outlook-callback-page">
      <CircularProgress />
    </div>
  );
};

export default OutlookCallback;
